/*
 * Pestaña Detalle: tabla filtrable y ordenable de los DO pendientes,
 * réplica del tab-detalle del HTML original.
 */
import { useMemo, useState } from "react";
import { Input, Select } from "codekit";

import Panelbox from "./Panelbox";
import Simtable, { Valspanx } from "./Simtable";
import { fmtCOP } from "./utils/carthelp";

/**
 * Construye las opciones de un select a partir de los valores únicos de una clave
 * @param {Array} mData Datos de trabajo
 * @param {string} sKey Clave a listar
 * @param {string} sAll Texto de la opción "todos"
 * @returns {Array} Opciones [{ value, text }]
 */
const buildOptions = (mData, sKey, sAll) => {
  const mVals = [...new Set(mData.map((r) => r[sKey]))].filter(Boolean).sort();
  return [{ value: "", text: sAll }, ...mVals.map((v) => ({ value: v, text: v }))];
};

const columns = [
  { key: "DO", title: "DO" },
  { key: "NOMBRE", title: "Cliente" },
  { key: "GERENCIA", title: "Gerencia" },
  { key: "DIRECTOR DE CUENTA", title: "Director de Cuenta" },
  { key: "TIPO DE OPERACIÓN", title: "Tipo" },
  { key: "_fechaCreacionStr", title: "Fecha Creación" },
  { key: "_fechaLevanteStr", title: "Fecha Levante" },
  { key: "_dias", title: "Días", render: (r) => r._dias ?? "-" },
  { key: "VALOR", title: "Valor", render: (r) => <Valspanx value={r.VALOR} text={fmtCOP(r.VALOR)} /> },
];

//Claves ordenables al hacer click en el encabezado (las mismas del HTML)
const sortableKeys = ["DO", "NOMBRE", "GERENCIA", "DIRECTOR DE CUENTA", "TIPO DE OPERACIÓN", "_dias", "VALOR"];

/**
 * Renderiza la pestaña Detalle
 * @param {Object} props Propiedades del componente
 * @param {Array} props.data Datos de trabajo filtrados
 */
const Tabdetal = ({ data }) => {
  const [sSearch, setSearch] = useState("");
  const [sGerencia, setGerencia] = useState("");
  const [sDirector, setDirector] = useState("");
  const [sTipo, setTipo] = useState("");
  const [sSortKey, setSortKey] = useState("VALOR");
  const [nSortDir, setSortDir] = useState(-1);

  //Alterna la clave y direccion de ordenamiento (igual que sortTable del HTML)
  const handleSort = (sKey) => {
    if (!sortableKeys.includes(sKey)) return;
    if (sSortKey === sKey) {
      setSortDir((nPrev) => nPrev * -1);
      return;
    }
    setSortKey(sKey);
    setSortDir(-1);
  };

  const mRows = useMemo(() => {
    const sQuery = sSearch.toLowerCase();
    const mFiltered = data.filter(
      (r) =>
        (!sQuery || r.NOMBRE.toLowerCase().includes(sQuery)) &&
        (!sGerencia || r.GERENCIA === sGerencia) &&
        (!sDirector || r["DIRECTOR DE CUENTA"] === sDirector) &&
        (!sTipo || r["TIPO DE OPERACIÓN"] === sTipo),
    );
    mFiltered.sort((a, b) => {
      const vA = a[sSortKey];
      const vB = b[sSortKey];
      if (typeof vA === "string") return nSortDir * vA.localeCompare(vB);
      return nSortDir * ((vA || 0) - (vB || 0));
    });
    return mFiltered.slice(0, 500);
  }, [data, sSearch, sGerencia, sDirector, sTipo, sSortKey, nSortDir]);

  return (
    <Panelbox title="Detalle de DO Pendientes" hint="Filtra y explora cada registro individual">
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ minWidth: 180 }}>
          <Input placeholder="Buscar cliente..." value={sSearch} disableUppercase onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div style={{ minWidth: 180 }}>
          <Select options={buildOptions(data, "GERENCIA", "Todas las gerencias")} value={sGerencia} onChangeValue={({ option }) => setGerencia(option.value)} />
        </div>
        <div style={{ minWidth: 180 }}>
          <Select options={buildOptions(data, "DIRECTOR DE CUENTA", "Todos los directores")} value={sDirector} onChangeValue={({ option }) => setDirector(option.value)} />
        </div>
        <div style={{ minWidth: 180 }}>
          <Select options={buildOptions(data, "TIPO DE OPERACIÓN", "Todos los tipos")} value={sTipo} onChangeValue={({ option }) => setTipo(option.value)} />
        </div>
      </div>
      <Simtable columns={columns} rows={mRows} onHeaderClick={handleSort} />
    </Panelbox>
  );
};

export default Tabdetal;
